import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MDText } from 'i18n-react';

import IconButton from '@adsk/bim360-matrix-react-icon-button';

import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';

import './_TablePagination.scss';

export class TablePagination extends React.PureComponent {
  constructor(props) {
    super(props);
    this.firstSvg = this.firstSvg.bind(this);
    this.previousSvg = this.previousSvg.bind(this);
    this.pageButton = this.pageButton.bind(this);
  }

  getTransform(rotate) {
    return rotate ? 'translate(16, 16) rotate(180)' : undefined;
  }

  firstSvg(rotate) {
    return (
      <svg width="16px" height="16px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
        <g
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
          transform={this.getTransform(rotate)}
        >
          <g fill="currentColor">
            <polygon points="12.9643495 4.07405031 12.1259923 3.26194728 6.96434949 8.26194728 12.1259923 13.2619473 12.9643495 12.4498442 8.64106381 8.26194728" />
            <polygon points="7.96434949 4.07405031 7.12599233 3.26194728 1.96434949 8.26194728 7.12599233 13.2619473 7.96434949 12.4498442 3.64106381 8.26194728" />
          </g>
        </g>
      </svg>
    );
  }

  previousSvg(rotate) {
    return (
      <svg width="16px" height="16px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
        <g
          id="Symbols"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
          transform={this.getTransform(rotate)}
        >
          <g fill="currentColor">
            <polygon points="9.84697589 3.81697568 9.03000021 3 4 8.03000021 9.03000021 13.0600004 9.84697589 12.2430247 5.63395137 8.03000021" />
          </g>
        </g>
      </svg>
    );
  }

  pageButton(pageLink, className, svg) {
    const classes = classNames('TablePagination__navigation-button', className);
    return (
      <IconButton
        className={classes}
        styleType={IconButton.StyleType.MINIMAL}
        onClick={
          pageLink
            ? () => {
              this.props.onPageChange(pageLink);
            }
            : null
        }
        disabled={!pageLink}
      >
        {svg}
      </IconButton>
    );
  }

  render() {
    const {
      limit,
      offset,
      total,
      firstLink,
      previousLink,
      nextLink,
      lastLink,
      paginationCounterSeparatorLabel,
      paginationDescription,
      className
    } = this.props;

    // Calc page display values and options from pagination metadata
    const pageStartIndex = offset + 1;
    const pageEndIndex = offset + (total - offset < limit ? total - offset : limit);
    const currentPageIndex = Math.floor(offset / limit) + 1;
    const totalNumberOfPages = Math.ceil(total / limit);

    const hasBefore = offset !== 0;
    const hasAfter = total - offset > limit;
    const baseLinkPayload = { limit, total, offset };

    const firstLinkMetadata =
      'firstLink' in this.props
        ? firstLink
        : hasBefore && { ...baseLinkPayload, link: TablePagination.PageLinks.FIRST };
    const previousLinkMetadata =
      'previousLink' in this.props
        ? previousLink
        : hasBefore && { ...baseLinkPayload, link: TablePagination.PageLinks.PREVIOUS };
    const nextLinkMetadata =
      'nextLink' in this.props
        ? nextLink
        : hasAfter && { ...baseLinkPayload, link: TablePagination.PageLinks.NEXT };
    const lastLinkMetadata =
      'lastLink' in this.props
        ? lastLink
        : hasAfter && { ...baseLinkPayload, link: TablePagination.PageLinks.LAST };

    const firstButton = this.pageButton(
      firstLinkMetadata,
      'TablePagination__first-page-button',
      this.firstSvg()
    );
    const previousButton = this.pageButton(
      previousLinkMetadata,
      'TablePagination__previous-page-button',
      this.previousSvg()
    );
    const nextButton = this.pageButton(
      nextLinkMetadata,
      'TablePagination__next-page-button',
      this.previousSvg(true)
    );
    const lastButton = this.pageButton(
      lastLinkMetadata,
      'TablePagination__last-page-button',
      this.firstSvg(true)
    );

    const paginationDescriptionText = paginationDescription
      ? paginationDescription({ pageStartIndex, pageEndIndex, total })
      : this.props[TSymbolProp].translate('PAGINATION_DESCRIPTION', {
        pageStartIndex,
        pageEndIndex,
        total
      });

    return (
      <div className={classNames('TablePagination', className)}>
        <span>{paginationDescriptionText}</span>
        <div className="TablePagination__right-side">
          {firstButton}
          {previousButton}
          <span>
            <span className="TablePagination__page-number-text">{currentPageIndex}</span>
            <span>
              {paginationCounterSeparatorLabel
                ? paginationCounterSeparatorLabel
                : this.props[TSymbolProp].translate('PAGINATION_COUNTER_SEPARATOR_LABEL')}
            </span>
            <span className="TablePagination__page-number-text">{totalNumberOfPages}</span>
          </span>
          {nextButton}
          {lastButton}
        </div>
      </div>
    );
  }
}

TablePagination.PageLinks = {
  FIRST: 'first',
  LAST: 'last',
  NEXT: 'next',
  PREVIOUS: 'previous'
};

TablePagination.propTypes = {
  /**
   * The number of rows displayed in each page of the table.
   */
  limit: PropTypes.number.isRequired,
  /**
   * The number of the first row that starts the current page.
   */
  offset: PropTypes.number.isRequired,
  /**
   * The total number of rows.
   */
  total: PropTypes.number.isRequired,
  /**
   * Handler that's called when the page needs to change. (pageLink) => *
   */
  onPageChange: PropTypes.func.isRequired,
  /**
   * Function that calculates the custom pagination description. ({pageStartIndex: Number, pageEndIndex: Number, total: Number}) => String
   */
  paginationDescription: PropTypes.func,
  /**
   * String representing the separator between the current page count and the total page count.
   */
  paginationCounterSeparatorLabel: PropTypes.string,
  /**
   * The link to the first page in the entire collection.
   */
  firstLink: PropTypes.string,
  /**
   * The link to the last page in the entire collection.
   */
  lastLink: PropTypes.string,
  /**
   * The link to the next page in the entire collection.
   */
  nextLink: PropTypes.string,
  /**
   * The link to the previous page in the entire collection.
   */
  previousLink: PropTypes.string,
  /**
   * A custom className that's applied to the entire pagination block.
   */
  className: PropTypes.string,
  /**
   * The locale to use for translated strings
   */
  locale: PropTypes.string
};

export default withLocale(
  TablePagination,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
